import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { TextValidator, SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import { find } from 'lodash';
import AlertDialog from '../../../../../../components/AlertDialog/index.es6';
import {
  getLocalNumberLength,
  getPartner,
  isVAlidBotswanaNationalId,
  isVAlidSouthAfricanNationalId
} from '../../../../../../lib/access.es6';
import { formatDate } from '../../../../../../lib/utils.es6';
import { getPolicyDependents } from '../../../../../../lib/requests.es6';
import {
  getNuclearBenefit,
  getNuclearPremium,
  getAgeRangeObject,
  isExtendedMember
} from '../../../../PolicyAdmin/helpers';

const AddMemberFormModal = ({
  show,
  onClose,
  onAddMember,
  relationship,
  memberType,
  nuclearPlan,
  parentPlan,
  policy,
  currentMembers,
  addedMembers
}) => {
  const [firstname, setFirstname] = useState('');
  const [middlename, setMiddleName] = useState('');
  const [lastname, setLastname] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [phonenumber, setPhoneNumber] = useState('');
  const [nationalid, setNationalId] = useState('');
  const [gender, setGender] = useState('');
  const [postalAddress, setpostalAddress] = useState('');
  const [residentialAddress, setresidentialAddress] = useState('');
  const [southAfricanIdCheckError, setSouthAfricanIdCheckError] = useState(null);

  const [duplicateMembers, setDuplicateMembers] = useState(null);
  const [checkingDuplicateMembers, setCheckingDuplicateMembers] = useState(false);
  const [duplicateMemberFound, setDuplicateMemberFound] = useState(false);

  const [ageRanges, setAgeRanges] = useState(null);
  const [extendedMemberPremiumNotFound, setExtendedMemberPremiumNotFound] = useState(false);

  const [options, setOptions] = useState([]);
  const [selectedExtendedBenefit, setSelectedExtendedBenefit] = useState('');

  const formRef = useRef(null);
  const internationalCallingCode = getPartner().country.international_calling_code;

  // Get member config from plan
  const getMemberConfig = (rel) => {
    const plan = memberType === 'parent' ? (parentPlan || nuclearPlan) : nuclearPlan;
    if (!plan || !plan.membership_configs || !plan.membership_configs.content) return null;

    const allConfigs = [
      ...(plan.membership_configs.content.nuclear || []),
      ...(plan.membership_configs.content.extended || [])
    ];

    return allConfigs.find(c => c.relationship === rel);
  };

  // Count relationships in current + added members
  const countRelationships = (rel) => {
    const allMembers = [...currentMembers, ...addedMembers];
    return allMembers.filter(m => m.relationship === rel).length;
  };

  // Get extended member config based on age
  const getExtendedMemberConfig = (dob) => {
    const plan = parentPlan || nuclearPlan;
    if (!plan || !plan.membership_configs || !plan.membership_configs.content) return null;

    const currentYear = new Date().getFullYear();
    const birthYear = new Date(dob).getFullYear();
    const age = currentYear - birthYear;

    // Get premiums from the plan's membership_configs.content.premiums array
    const premiums = plan.membership_configs.content.premiums || [];

    // Find the premium tier that matches the age
    for (const premium of premiums) {
      if (age >= premium.entry_age && age <= premium.exit_age) {
        return {
          premium_rate: premium.premium_rate,
          benefit_amount: premium.benefit_amount
        };
      }
    }

    return null;
  };

  // Fetch duplicate members check
  const fetchDuplicateMembers = async (national_id) => {
    if (!national_id) {
      addMemberToList();
      return;
    }

    setDuplicateMemberFound(false);
    setCheckingDuplicateMembers(true);

    try {
      // Check locally first
      const allMembers = [...currentMembers, ...addedMembers];
      const duplicate = find(allMembers, { national_id: nationalid });

      if (duplicate) {
        setDuplicateMembers({ active: true, local: true, duplicate });
        setDuplicateMemberFound(true);
        setCheckingDuplicateMembers(false);
        return;
      }

      // Check API
      const response = await getPolicyDependents(national_id);
      setDuplicateMembers({ ...response.data, local: false });

      if (response.data.active) {
        setDuplicateMemberFound(true);
      } else {
        setDuplicateMemberFound(false);
        addMemberToList();
      }
    } catch (error) {
      console.error('Error fetching duplicate members:', error);
    }

    setCheckingDuplicateMembers(false);
  };

  // Get nuclear benefits for a member
  const getNuclearBenefits = (rel) => {
    const coverType = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.cover_type
      : 'Member & Family';

    const nukPremium = getNuclearPremium(nuclearPlan, coverType);
    const nukBenefit = getNuclearBenefit(nuclearPlan);
    const accidentalBenefitAmount = 10000000; // 100,000 BWP

    const isPrincipal = rel === 'Principal';

    const benefits = [];

    if (rel === 'Principal' || rel === 'Spouse') {
      const funeral_uuid = uuidv4();
      const accidental_uuid = uuidv4();

      benefits.push({
        type: 'FUNERAL BENEFIT',
        cover_amount_in_cents: nukBenefit,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: 'year',
        premium_amount: isPrincipal ? nukPremium : 0,
        uuid: funeral_uuid,
        is_initialized: true,
        additional_details: {
          premium_amount: isPrincipal ? nukPremium : 0,
          uuid: funeral_uuid
        }
      });

      benefits.push({
        type: 'ACCIDENTAL BENEFIT',
        cover_amount_in_cents: nukBenefit > accidentalBenefitAmount ? accidentalBenefitAmount : nukBenefit,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: 'year',
        premium_amount: 0,
        uuid: accidental_uuid,
        is_initialized: true,
        additional_details: {
          premium_amount: 0,
          uuid: accidental_uuid
        }
      });
    } else if (rel === 'Child') {
      const child_uuid_funeral = uuidv4();

      benefits.push({
        type: 'FUNERAL BENEFIT',
        cover_amount_in_cents: nukBenefit,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: 'year',
        premium_amount: 0,
        uuid: child_uuid_funeral,
        is_initialized: true,
        additional_details: {
          premium_amount: 0,
          uuid: child_uuid_funeral
        }
      });
    }

    return benefits;
  };

  // Add member to the list
  const addMemberToList = () => {
    const isExtended = isExtendedMember(relationship);
    const memberUuid = uuidv4();

    // Extended member (user-selected benefit tier)
    if (isExtended) {
      if (!selectedExtendedBenefit) {
        alert('Please select a benefit tier for extended member');
        return;
      }

      const extended_uuid_funeral = uuidv4();

      const payload = {
        guid: memberUuid, // For API compatibility
        first_name: firstname,
        middle_name: middlename,
        last_name: lastname,
        msisdn: phonenumber ? internationalCallingCode + phonenumber : 'N/A',
        date_of_birth: dateOfBirth,
        gender: gender,
        national_id: nationalid || 'N/A',
        passport: 'N/A',
        postal_address: postalAddress || '',
        residential_address: residentialAddress || '',
        relationship: relationship,
        type: 'Extended_2',
        premium_amount_in_cents: selectedExtendedBenefit.premium_amount * 100,
        extended_premium: 0,
        add_on_premium: 0,
        benefits: [{
          type: 'FUNERAL BENEFIT',
          cover_amount_in_cents: selectedExtendedBenefit.benefit_amount * 100,
          waiting_period_in_days: 30,
          cover_usage_count: 0,
          benefit_period_cardinality: 1,
          benefit_period_granuality: 'year',
          premium_amount: selectedExtendedBenefit.premium_amount * 100,
          uuid: extended_uuid_funeral,
          is_initialized: true,
          additional_details: {
            premium_amount: selectedExtendedBenefit.premium_amount * 100,
            uuid: extended_uuid_funeral
          }
        }],
        uuid: memberUuid,
        additional_details: {
          uuid: memberUuid,
          memberType: 'Extended'
        }
      };

      onAddMember(payload);
      resetForm();
      return;
    }

    // Determine member type
    const isNuclear = ['Principal', 'Spouse', 'Child'].includes(relationship);
    const isParent = ['Parent', 'Parent_In_Law'].includes(relationship);

    let benefits = [];
    let memberPremium = 0;

    if (isNuclear) {
      benefits = getNuclearBenefits(relationship);
      memberPremium = benefits.reduce((sum, b) => sum + (b.premium_amount || 0), 0);
    } else if (isParent) {
      const extendedConfig = getExtendedMemberConfig(dateOfBirth);

      if (!extendedConfig) {
        setExtendedMemberPremiumNotFound(true);
        return;
      }

      const parent_uuid_funeral = uuidv4();

      benefits = [{
        type: 'FUNERAL BENEFIT',
        cover_amount_in_cents: extendedConfig.benefit_amount,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: 'year',
        premium_amount: extendedConfig.premium_rate,
        is_initialized: true,
        uuid: parent_uuid_funeral,
        additional_details: {
          premium_amount: extendedConfig.premium_rate,
          uuid: parent_uuid_funeral
        }
      }];

      memberPremium = extendedConfig.premium_rate;
    }

    const payload = {
      guid: memberUuid, // For API compatibility
      first_name: firstname,
      middle_name: middlename,
      last_name: lastname,
      msisdn: phonenumber ? internationalCallingCode + phonenumber : 'N/A',
      date_of_birth: dateOfBirth,
      gender: gender,
      national_id: nationalid || 'N/A',
      passport: 'N/A',
      postal_address: postalAddress || '',
      residential_address: residentialAddress || '',
      relationship: relationship,
      type: isNuclear ? 'Nuclear' : 'Extended',
      premium_amount_in_cents: memberPremium,
      extended_premium: 0,
      add_on_premium: 0,
      benefits: benefits,
      uuid: memberUuid,
      additional_details: {
        uuid: memberUuid,
        memberType: isNuclear ? 'Nuclear' : (isParent ? 'Parent' : 'Extended')
      }
    };

    onAddMember(payload);
    resetForm();
  };

  const resetForm = () => {
    setFirstname('');
    setMiddleName('');
    setLastname('');
    setDateOfBirth(null);
    setPhoneNumber('');
    setNationalId('');
    setGender('');
    setpostalAddress('');
    setresidentialAddress('');
    setSouthAfricanIdCheckError(null);
    setDuplicateMembers(null);
    setDuplicateMemberFound(false);
    setExtendedMemberPremiumNotFound(false);
    setSelectedExtendedBenefit('');
    onClose();
  };

  // Update extended benefit options when DOB changes
  useEffect(() => {
    if (dateOfBirth && isExtendedMember(relationship)) {
      const rangeObject = getAgeRangeObject(dateOfBirth);
      if (rangeObject) {
        setOptions(rangeObject.options);
      } else {
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  }, [dateOfBirth, relationship]);

  // Validation rules
  useEffect(() => {
    ValidatorForm.addValidationRule('isValidPhoneNumber', value => {
      return value.length === getLocalNumberLength();
    });

    ValidatorForm.addValidationRule('isValidNationalId', (value) => {
      if (['Child', 'Spouse', 'Parent', 'Parent_In_Law'].includes(relationship) && value === '') {
        return true;
      }

      if (isExtendedMember(relationship) && value === '') {
        return true;
      }

      const partnerGuid = getPartner().guid;
      if (partnerGuid === 'aspinplus') {
        const result = isVAlidSouthAfricanNationalId(value);
        if (result.isValid) {
          setDateOfBirth(formatDate(new Date(result.dob)));
          setGender(result.gender);
          setSouthAfricanIdCheckError(result.error);
          return true;
        } else {
          setSouthAfricanIdCheckError(result.error);
          setGender('');
          setDateOfBirth('');
          return false;
        }
      } else {
        const result = isVAlidBotswanaNationalId(value);
        if (result.isValid) {
          setGender(result.gender);
          setSouthAfricanIdCheckError(result.error);
          return true;
        } else {
          setSouthAfricanIdCheckError(result.error);
          setGender('');
          return false;
        }
      }
    });

    ValidatorForm.addValidationRule('isValidDateOfBirth', (dobValue) => {
      if (!dateOfBirth) return true;
      const memberConfig = getMemberConfig(relationship);
      if (!memberConfig) return true;

      const maximum_age = memberConfig.age_range.max;
      const minimum_age = memberConfig.age_range.min;

      const selectedDateObj = new Date(dobValue);
      const currentDate = new Date();

      const ageDifference = currentDate.getFullYear() - selectedDateObj.getFullYear();

      setAgeRanges({ minimum_age, maximum_age });
      return ageDifference >= minimum_age && ageDifference <= maximum_age;
    });

    ValidatorForm.addValidationRule('hasMaximumMemberCount', (rel) => {
      const memberConfig = getMemberConfig(rel);
      if (!memberConfig) return true;

      const maximumMembersAllowed = memberConfig.max_count;
      const totalMemberTypeAdded = countRelationships(rel);

      if (totalMemberTypeAdded >= maximumMembersAllowed) return false;
      return true;
    });

    return function cleanup() {
      ValidatorForm.removeValidationRule('isValidPhoneNumber');
      ValidatorForm.removeValidationRule('isValidNationalId');
      ValidatorForm.removeValidationRule('isValidDateOfBirth');
      ValidatorForm.removeValidationRule('hasMaximumMemberCount');
    };
  }, [relationship, dateOfBirth, currentMembers, addedMembers]);

  const handleSubmit = () => {
    fetchDuplicateMembers(nationalid);
  };

  const handleOptionChange = (event) => {
    setSelectedExtendedBenefit(event.target.value);
  };

  return (
    <AlertDialog
      custom
      show={show}
      onClose={resetForm}
      size="lg"
      showCancel={false}
      showConfirm={false}
      showTitle={false}
      style={{ marginTop: '0', top: '50%', transform: 'translateY(-50%)', maxHeight: '90vh', overflow: 'visible', width: '900px', maxWidth: '90vw' }}
    >
      <div style={{ padding: '30px', maxHeight: '82vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: '20px', color: '#032A37' }}>
          Add {relationship ? relationship.replace(/_/g, ' ') : 'Member'}
        </h2>

        <ValidatorForm ref={formRef} onSubmit={handleSubmit} instantValidate={false}>
          <Grid container spacing={3}>
            {/* First Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="First Name"
                onChange={(e) => setFirstname(e.target.value)}
                name="firstname"
                value={firstname}
                validators={['required']}
                errorMessages={['First name is required']}
                variant="outlined"
              />
            </Grid>

            {/* Middle Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Middle Name (Optional)"
                onChange={(e) => setMiddleName(e.target.value)}
                name="middlename"
                value={middlename}
                variant="outlined"
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Last Name"
                onChange={(e) => setLastname(e.target.value)}
                name="lastname"
                value={lastname}
                validators={['required']}
                errorMessages={['Last name is required']}
                variant="outlined"
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Date of Birth"
                onChange={(e) => setDateOfBirth(e.target.value)}
                name="dateOfBirth"
                type="date"
                value={dateOfBirth || ''}
                validators={['required', 'isValidDateOfBirth']}
                errorMessages={[
                  'Date of birth is required',
                  ageRanges
                    ? `Age must be between ${ageRanges.minimum_age} and ${ageRanges.maximum_age} years`
                    : 'Invalid age for this relationship'
                ]}
                variant="outlined"
                
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12} md={6}>
              <SelectValidator
                fullWidth
                label="Gender"
                onChange={(e) => setGender(e.target.value)}
                name="gender"
                value={gender}
                validators={['required']}
                errorMessages={['Gender is required']}
                variant="outlined"
                
                SelectProps={{
                  MenuProps: {
                    disablePortal: false,
                    style: { zIndex: 99999 },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }
                }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </SelectValidator>
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Phone Number (Optional)"
                onChange={(e) => setPhoneNumber(e.target.value)}
                name="phonenumber"
                value={phonenumber}
                validators={phonenumber ? ['isValidPhoneNumber'] : []}
                errorMessages={[`Phone number must be ${getLocalNumberLength()} digits`]}
                variant="outlined"

              />
            </Grid>

            {/* National ID */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label={`National ID ${['Child', 'Spouse', 'Parent', 'Parent_In_Law'].includes(relationship) ? '(Optional)' : ''}`}
                onChange={(e) => setNationalId(e.target.value)}
                name="nationalid"
                value={nationalid}
                validators={['isValidNationalId']}
                errorMessages={[southAfricanIdCheckError || 'Invalid National ID format']}
                variant="outlined"
                
              />
            </Grid>

            {/* Residential Address */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Residential Address (Optional)"
                onChange={(e) => setresidentialAddress(e.target.value)}
                name="residentialAddress"
                value={residentialAddress}
                validators={[]}
                errorMessages={[]}
                variant="outlined"

              />
            </Grid>

            {/* Postal Address */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Postal Address (Optional)"
                onChange={(e) => setpostalAddress(e.target.value)}
                name="postalAddress"
                value={postalAddress}
                variant="outlined"
                
              />
            </Grid>

            {/* Extended Member Benefit Selection */}
            {isExtendedMember(relationship) && options.length > 0 && (
              <Grid item xs={12}>
                <SelectValidator
                  fullWidth
                  label="Select Benefit Tier"
                  onChange={handleOptionChange}
                  name="extendedBenefit"
                  value={selectedExtendedBenefit}
                  validators={['required']}
                  errorMessages={['Please select a benefit tier']}
                  variant="outlined"
                  
                  SelectProps={{
                    MenuProps: {
                      disablePortal: false,
                      style: { zIndex: 99999 },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                >
                  {options.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      Benefit: BWP {(option.benefit_amount || 0).toLocaleString()} - Premium: BWP {(option.premium_amount || 0).toFixed(2)}
                    </MenuItem>
                  ))}
                </SelectValidator>
              </Grid>
            )}
          </Grid>

          {/* Error Messages */}
          {extendedMemberPremiumNotFound && (
            <div style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px'
            }}>
              No premium found for this age range. Please contact support.
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={checkingDuplicateMembers}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={checkingDuplicateMembers}
              style={{ backgroundColor: '#032A37', color: '#fff' }}
            >
              {checkingDuplicateMembers ? <CircularProgress size={24} /> : 'Add Member'}
            </Button>
          </div>
        </ValidatorForm>

        {/* Duplicate Member Dialog */}
        <AlertDialog
          warning
          show={duplicateMemberFound}
          size="sm"
          title="Duplicate Member Found"
          style={{ marginTop: '0', top: '30vh' }}
          onConfirm={() => {
            setDuplicateMemberFound(false);
            addMemberToList();
          }}
          onCancel={() => setDuplicateMemberFound(false)}
          confirmBtnText="Add Anyway"
          showCancel={true}
          showConfirm={true}
          cancelBtnText="Cancel"
        >
          <p>A member with this National ID already exists in {duplicateMembers && duplicateMembers.local ? 'this policy' : 'another policy'}.</p>
          <p>Do you want to add them anyway?</p>
        </AlertDialog>
      </div>
    </AlertDialog>
  );
};

export default AddMemberFormModal;
